import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import CustomLoading from '../../util_components/custom_loading';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import { is_terminal } from './overdue';
import { closed_by_line } from './closed_by';
import StackTd from './StackTable';
import { stackClasses } from './stack_table';

const ST = stackClasses();

const STATUS_BADGE = {
  draft: { cls: 'bg-amber-50 text-amber-700', label: 'Draft' },
  submitted: { cls: 'bg-green-50 text-green-700', label: 'Completed' },
  pending: { cls: 'bg-gray-100 text-gray-600', label: 'Pending' },
  // Closed by the system, not by an agent — the student converted to a member and
  // goserver's student_became_member lifecycle hook closed their open tasks. The
  // popup lists every task for the student, so unlike the list page it always
  // shows these.
  auto_closed: { cls: 'bg-gray-100 text-gray-500', label: 'Auto closed' },
  auto_completed: { cls: 'bg-gray-100 text-gray-500', label: 'Auto completed' },
  skipped: { cls: 'bg-gray-100 text-gray-500', label: 'Skipped' }
};

// Per-student sales tasks (one row per task), shown in a popup from the
// "Sales Task List" button on the student profile. Existing tasks link to the
// standalone task detail view; missing ones offer a "Create" action that
// generates the task on the fly.
class SalesTasks extends Component {
  state = { items: [], loading: false, error: '', creating: {} };

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ loading: true, error: '' });
    const url = get_api_url(`/v2/sales_task_list/student_tasks/${this.props.student_uuid}`);
    get_api(url, true)
      .then((res) => {
        this.setState({ items: res.data.data.items || [], loading: false });
      })
      .catch((e) => {
        let err = 'Failed to load sales tasks';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err, loading: false });
      });
  };

  // Generate a task for this student on the fly, then open its detail view
  // and refresh the popup.
  create_task = (item) => {
    this.setState((prev) => ({ creating: { ...prev.creating, [item.task_type]: true } }));
    const url = get_api_url('/v2/sales_task_list/create_task');
    post_api(url, { student_uuid: this.props.student_uuid, task_type: item.task_type }, true)
      .then((res) => {
        const uuid = res?.data?.data?.uuid;
        if (uuid) {
          window.open(
            `/sales-task-form?uuid=${encodeURIComponent(uuid)}`,
            '_blank',
            'noopener,noreferrer'
          );
        }
        this.load();
      })
      .catch((e) => {
        let err = 'Failed to create sales task';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err });
      })
      .finally(() =>
        this.setState((prev) => {
          const creating = { ...prev.creating };
          delete creating[item.task_type];
          return { creating };
        })
      );
  };

  // eslint-disable-next-line class-methods-use-this
  task_badge = (item) => (
    <span
      className="inline-block max-w-full truncate align-middle px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
      title={item.task_label || item.task_type || undefined}
    >
      {item.task_label || item.task_type || '—'}
    </span>
  );

  // eslint-disable-next-line class-methods-use-this
  status_badge = (status) => {
    const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  render_row = (item) => {
    const creating = !!this.state.creating[item.task_type];
    return (
      <tr
        key={item.uuid || item.task_type}
        className={`border-t border-line hover:bg-surface-sunken ${ST.row}`}
      >
        <StackTd className="text-left px-3 py-2.5">{this.task_badge(item)}</StackTd>
        <StackTd label="Due" className="text-left px-3 py-2.5 text-content-secondary">
          {item.due_datetime || '—'}
        </StackTd>
        <StackTd label="Status" className="text-left px-3 py-2.5">
          {item.exists ? (
            <>
              {this.status_badge(item.status)}
              {closed_by_line(item) ? (
                <div className="mt-1 text-xs text-content-muted">{closed_by_line(item)}</div>
              ) : null}
            </>
          ) : (
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Not created
            </span>
          )}
        </StackTd>
        <StackTd className="text-left px-3 py-2.5">
          {item.exists ? (
            <Link
              to={`/sales-task-form?uuid=${encodeURIComponent(item.uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              {is_terminal(item) ? 'View' : 'Open task'}
            </Link>
          ) : (
            <button
              type="button"
              disabled={creating}
              onClick={() => this.create_task(item)}
              className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-wait transition-colors"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
          )}
        </StackTd>
      </tr>
    );
  };

  render() {
    const { items, loading, error } = this.state;
    if (loading) return <CustomLoading />;

    let body;
    if (error) {
      body = (
        <div className="text-sm" style={{ color: 'var(--state-error-text, #b91c1c)' }}>
          {error}
        </div>
      );
    } else if (items.length === 0) {
      body = (
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          No sales tasks for this member yet.
        </div>
      );
    } else {
      body = (
        <table className={ST.table}>
          <thead className={`bg-surface-sunken text-content-secondary ${ST.head}`}>
            <tr>
              <th className="w-[40%] text-left px-3 py-2 font-medium">Task</th>
              <th className="w-[22%] text-left px-3 py-2 font-medium">Due</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="w-[112px] text-left px-3 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody className={ST.body}>{items.map(this.render_row)}</tbody>
        </table>
      );
    }

    return <div>{body}</div>;
  }
}

SalesTasks.propTypes = {
  student_uuid: PropTypes.string.isRequired
};

export default SalesTasks;
